import {
  Facet,
  IFacet
} from '@ms/items-view/lib/private/models/Facet';
import { fieldsFacet } from '@ms/items-view/lib/private/facets/ItemFacets';
import { FieldType, IFieldData } from './interfaces/Fields';
import {
  ISPListRow,
  ISPListSchema,
  ISPContentType,
  ISPGetItemResponse,
  ISPListField,
  IGridInitInfo,
} from '@ms/odsp-datasources/lib/dataSources/item/spListItemRetriever/interfaces/ISPGetItemResponse';
import { IItemsViewListFacet, IListFormUrlsFacet } from './interfaces/List';
import { ColumnFieldType } from '@ms/odsp-datasources/lib/interfaces/list/ColumnFieldType';
import { IDouble } from '@ms/odsp-datasources/lib/interfaces/IDouble';
import ISpPageContext from '@ms/odsp-datasources/lib/interfaces/ISpPageContext';
import { IFeature } from '@ms/odsp-utilities/lib/features/FeaturesBasic';
import { IFilterOption } from './interfaces/Filter';
import { IViewFormatterFacet } from '../models/viewFormatter';
import { IServerView } from '@ms/odsp-datasources/lib/interfaces/view/IServerView';
import IView from '@ms/odsp-datasources/lib/interfaces/view/IView';
import { spViewKeyFacet } from './Keys';
import { IDataRequestorPageContext, IErrorData } from '@ms/odsp-datasources/lib/dataSources/base/DataRequestor';
import { ISpfxAdapter, IListViewSpfxHostApplication } from '@ms/odsp-datasources/lib/interfaces/clientSideExtensions/ISpfxAdapter';
import { ISPFrameworkClientInfo } from '@ms/odsp-datasources/lib/interfaces/clientSideExtensions/IClientSideExtensions';
import { IAggregateData } from '@ms/odsp-datasources/lib/dataSources/item/spListItemProcessor/ISPListItemData';
import { IPrefetchDataOptions } from './interfaces/IGetServerData';
import { IItemCacheThunkAction } from '@ms/items-view/lib/private/store/actions/ItemCacheActions';
import { IFilter } from '@ms/odsp-datasources/lib/interfaces/view/IViewArrangeInfo';
import { INewDocumentTemplate } from '../models/newDocumentTemplate';
import { PeoplePickerDataSource } from '@ms/odsp-datasources/lib/dataSources/peoplePicker/spo/PeoplePickerDataSource';
import { IItemCache } from '@ms/items-view/lib/private/models/IItemCacheObserver';
import { Signal } from '@ms/items-view/lib/private/utilities/async/Signal';
import { ModerationStatusType } from './interfaces/ModerationStatusType';

/**
 * Configuration facet for data fetching in addon-sp.
 * @public
 */
export const spDataSourceConfigurationFacet: Facet<{
  /**
   * Method to get an auth token, when requested. This is the required method for getting an auth token,
   * but `ensureAuthTokenWithAction` is preferred.
   * Takes a url describing the resource to get the token for and options.
   */
  ensureAuthToken: (url: string, options?: { disallowCookies?: boolean; }) => Promise<string | undefined>;

  /**
   * A function to get an auth token, when requested. This method is preferred as it is dispatched with an action. It will be invoked
   * rather than `ensureAuthToken`, if provided.
   * Takes a url describing the resource to get the token for and options.
   */
  ensureAuthTokenWithAction?: (url: string, options?: { disallowCookies?: boolean; }) => IItemCacheThunkAction<Promise<string>>;

  /** Whether need to set noRedirect option for dataRequestor to false. */
  dataRequestorAutoRedirect?: boolean;
}> = new Facet('spDataSourceConfiguration');

/**
 * Configuration facet for data fetching from SPHome in addon-sp.
 * @public
 */
export const spHomeDataSourceConfigurationFacet: Facet<{
  /** Whether or not the Context call should be used to get an auth token for SPHome */
  contextAcquiresAuthToken?: boolean;
}> = new Facet('spHomeDataSourceConfiguration');

/**
 * Facet indicating which domain may support cookie auth for certain requests.
 * @internal
 */
export const spCookieDomainConfigurationFacet: Facet<string> = new Facet('spCookieDomainConfiguration');

/**
 * Key facet for createDocument action auth mode information.
 * @internal
 */
export const spCreateDocumentFormDigestFacet: Facet<boolean> = new Facet('spCreateDocumentFormDigestFacet');

/**
 * @beta
 */
export const spPrefetchDataConfigurationFacet: Facet<{
  prefetchData: (options: IPrefetchDataOptions) => Promise<ISPGetItemResponse | undefined>;
  listDataUpdateCallback?: (url: string, body: string | undefined, listData: string) => void
}> = new Facet('prefetchDataConfiguration');

/**
 * @internal
 */
export const spFeatureConfigurationFacet: Facet<{
  isFeatureEnabled: (feature: IFeature, expFeatures: number[]) => boolean;
}> = new Facet('featureConfiguration');

/**
 * @internal
 */
export const spClientSideFrameworkConfigurationFacet: Facet<{
  loadSpfxHostApplication?: (SPFXClientInfo?: ISPFrameworkClientInfo) => Promise<IListViewSpfxHostApplication>;
  loadSpfxAdapter?: (SPFXClientInfo?: ISPFrameworkClientInfo) => Promise<ISpfxAdapter>;
  // Whether items scope needs to request Spfx client-side framework information when requst list data
  requestSpfxFrameworkInfo?: boolean;
}> = new Facet('clientSideFrameworkConfiguration');

/**
 * @internal
 */
export const internalNameFacet: Facet<string> = new Facet('internalName');

/**
 * This is corresponding to Type in ISPListField
 * @internal
 */
export const fieldTypeFacet: Facet<FieldType> = new Facet('fieldType');

/**
 * This is corresponding to FieldType in ISPListField.
 * For example for Taxonomy field allow multi value, FieldType is TaxonomyFieldTypeMulti
 * while Type is Lookup.
 * @internal
 */
export const fieldFieldTypeFacet: Facet<string> = new Facet('fieldFieldType');

/**
 * Mapped enum type based on the raw field schema.
 * @internal
 */
export const mappedColumnFieldTypeFacet: Facet<ColumnFieldType> = new Facet('mappedColumnFieldType');

/**
 * GUID of the field
 * @internal
 */
export const fieldIdFacet: Facet<string> = new Facet('columnId');

/**
 * Distinguishes a calculated field with return type T from a normal field of type T
 * @internal
 */
export const isCalculatedFacet: Facet<boolean> = new Facet('isCalculated');

/**
 * @internal
 */
export const isCountRelatedFacet: Facet<boolean> = new Facet('isCountRelated');

/**
 * @internal
 */
export const realFieldNameFacet: Facet<string> = new Facet('realFieldName');

/**
 * @internal
 */
export const titleFacet: Facet<string> = new Facet('title');

/**
 * @internal
 */
export const moderationStatusTypeFacet: Facet<ModerationStatusType> = new Facet('moderationStatusType');

/**
 * @internal
 */
export const moderationCommentsFacet: Facet<string> = new Facet('moderationComments');

/**
 * @internal
 */
export const folderPathFacet: Facet<string> = new Facet('folderPath');

/**
 * @internal
 */
export const uniqueIdFacet: Facet<string> = new Facet('uniqueId');

/**
 * @internal
 */
export const listItemIdFacet: Facet<string> = new Facet('listItemId');

/**
 * @internal
 */
export const largeFolderFacet: Facet<boolean> = new Facet('largeFolder');

/**
 * Facet which indicates that a folder item is a DocumentSet.
 * @internal
 */
export const documentSetFacet: Facet<{}> = new Facet('docSet');

/**
 * @internal
 */
export const customFormatterFacet: Facet<string> = new Facet('customFormatter');

/**
 * @internal
 */
export const rowDataFacet: Facet<Readonly<ISPListRow>> = new Facet('rowData');

/**
 * @internal
 */
export const listFacet: Facet<IItemsViewListFacet> = new Facet('listFacet');

/**
 * @internal
 */
export const listFormUrlsFacet: Facet<IListFormUrlsFacet> = new Facet('listFormUrlsFacet');

/**
 * @internal
 */
export const permissionsFacet: Facet<IDouble> = new Facet('permissions');

/**
 * @internal
 */
export const defaultGroupedFacet: Facet<boolean> = new Facet('defaultGrouped');

/**
 * @internal
 */
export const groupValueFacet: Facet<string> = new Facet('groupValue');

/**
 * @internal
 */
export const groupFilterByValueFacet: Facet<string> = new Facet('groupFilterByValue');

/**
 * @internal
 */
export const groupStringFacet: Facet<string> = new Facet('groupString');

/**
 * @internal
 */
export const parentGroupKeyFacet: Facet<{
  itemKey: string;
}> = new Facet('parentGroupKeyFacet');

/**
 * @internal
 */
export const spGroupFieldFacet: Facet<{
  internalName: string;
  fieldType: FieldType;
}> = new Facet('spGroupField');

/**
 * @internal
 */
export const spPageContextFacet: Facet<ISpPageContext> = new Facet('spPageContext');

/**
 * @internal
 */
export const spPageContextForDataRequestorFacet: Facet<IDataRequestorPageContext> = new Facet('spPageContextForDataRequestor');

/**
 * @internal
 */
export const filterOptionSetFacet: Facet<IFilterOption[]> = new Facet('filterOptionSet');

/**
 * @internal
 */
export const listSchemaFacet: Facet<ISPListSchema> = new Facet('listSchemaFacet');

/**
 * This is the facet that caches the value of the object that represents the ISPListSchema.CustomFormatter
 * @internal
 */
export const viewFormatterFacet: Facet<IViewFormatterFacet> = new Facet('viewFormatterFacet');

/**
 * @internal
 */
export const fieldDispFormUrlFacet: Facet<string> = new Facet('fieldDispFormUrl');

/**
 * @internal
 */
export const isReadOnlyFacet: Facet<boolean> = new Facet('isReadOnly');

/**
 * @internal
 */
export const taxonomyTermLabelFacet: Facet<string> = new Facet('taxonomyTermLabel');

/**
 * @internal
 */
export const spItemIdentifierFacet: Facet<string> = new Facet('spItemIdentifier');

/**
 * @internal
 */
export function getFieldFacet<TType extends FieldType>(internalName: string, type: TType): IFacet<IFieldData[TType]> {
  return fieldsFacet(internalName);
}

/**
 * @internal
 */
export const defaultDocumentLibraryUrlFacet: Facet<string> = new Facet('defaultDocumentLibraryUrl');

/**
 * @internal
 */
export const spListContentTypeFacet: Facet<ISPContentType> = new Facet('spListContentType');

/**
 * @internal
 */
export const spFolderContentTypeFacet: Facet<ISPContentType> = new Facet('spFolderContentType');

/**
 * @internal
 */
export const contentTypeKeysFacet: Facet<string[]> = new Facet('contentTypeKeys');

/**
 * @internal
 */
export const actionFacet: Facet<React.ComponentType<{}>> = new Facet('action');

/**
 * Server view object returned from server
 * @internal
 */
export const serverViewFacet: Facet<IServerView> = new Facet('serverView');

/** Derived IView object that is initialized from the serverView */
/**
 * @internal
 */
export const currentDerivedViewFacet: Facet<IView> = new Facet('currentDerivedView');

/**
 * Indicate whether we should show view modified indicator specifically.
 * Sort/filter/groupby action will set this facet to true.
 * And it will be reset to false once view is updated or user switch to the other view.
 * @internal
 */
export const showViewModifiedIndicatorFacet: Facet<boolean> = new Facet('showViewModifiedIndicator');

/**
 * @internal
 */
export const officeAppFacet: Facet<string> = new Facet('officeApp');

/**
 * Keep the raw server response of getItem call. We should only use this facet in the integration scenario.
 * For example in odsp-next, it need to process raw server response into different format.
 * @internal
 */
export const getItemResponseFacet: Facet<ISPGetItemResponse> = new Facet('getItemResponse');

/**
 * Keep the raw server response Json string of getItem call.
 * This is used to initialize quick edit layout.
 * @internal
 */
export const getItemResponseJsonStringFacet: Facet<string> = new Facet('getItemResponseJsonString');

/**
 * This is the facet that caches the default view key of a list.
 * @internal
 */
export const defaultViewFacet: Facet<{
  viewKey: string
}> = new Facet('defaultViewFacet');

/**
 * The prefered view of the contents of a folder.
 * @internal
 */
export const preferredViewFacet: Facet<{
  viewKey: typeof spViewKeyFacet.type
}> = new Facet('preferredViewFacet');

/**
 * Map containing the fields that are missing required information for a given item
 * @internal
 */
export const missingMetadataFieldsFacet: Facet<{ [key: string]: boolean }> = new Facet('missingMetadataFields');

/**
 * Item key which can be used to determine if an item set has items that need attention.
 * @internal
 */
export const needsAttentionItemFacet: Facet<string> = new Facet('needsAttentionItem');

/**
 * Indicate whether itemSet schema can be modified
 * @internal
 */
export const canModifySchemaFacet: Facet<boolean> = new Facet('canModifySchema');

/**
 * itemSet progid for the current folder
 * @internal
 */
export const progIdFacet: Facet<string> = new Facet('progId');

/**
 * Indicate whether istemSet is at rootFolder
 * @internal
 */
export const isRootFolderFacet: Facet<boolean> = new Facet('isRootFolder');

/**
 * Indicates which content type the item belongs to
 * @internal
 */
export const contentTypeIdFacet: Facet<string> = new Facet('contentTypeId');

/**
 * Caches the SPHome context resource url
 * @internal
 */
export const spHomeResourceFacet: Facet<string> = new Facet('spHomeResourceFacet');

/**
 * Caches the SPHome context payload header
 * @internal
 */
export const spHomePayloadHeaderFacet: Facet<string> = new Facet('spHomePayloadHeaderFacet');

/**
 * Facet containing a site's acronym
 * @internal
 */
export const acronymFacet: Facet<string> = new Facet('acronymFacet');

/**
 * Facet containing a site's banner color
 * @internal
 */
export const bannerColorFacet: Facet<string> = new Facet('bannerColorFacet');

/**
 * Facet containing a site's banner image url
 * @internal
 */
export const bannerImageUrlFacet: Facet<string> = new Facet('bannerImageUrlFacet');

/**
 * Facet containing a string for a fabric icon
 * @internal
 */
export const iconFacet: Facet<string> = new Facet('iconFacet');

/**
 * Facet indicating whether this item is a site
 * @internal
 */
export const isSiteFacet: Facet<boolean> = new Facet('isSiteFacet');

/**
 * storing column totals data
 * @internal
 */
export const columnTotalsFacet: Facet<{ [index: string]: IAggregateData; }> = new Facet('columnTotals');

/**
 * Friendly display text for the date modified field
 * @internal
 */
export const friendlyDateModifiedFacet: Facet<string> = new Facet('friendlyDateModified');

/**
 * Facet indicate launch behavior for promotedLinksList item
 * @internal
 */
export const launchBehaviorFacet: Facet<string> = new Facet('launchBehavior');

/**
 * Facet indicate link location for promotedLinksList item
 * @internal
 */
export const linkLocationFacet: Facet<string> = new Facet('linkLocation');

/**
 * The full ISPListField metadata which represents information about a metadata column
 * @internal
 */
export const fieldDataFacet: Facet<ISPListField> = new Facet('fieldDataFacet');

/**
 * @internal
 */
export const spTenantAdminFacet: Facet<boolean> = new Facet('spTenantAdmin');

/**
 * @internal
 */
export const spErrorDataFacet: Facet<IErrorData> = new Facet('spErrorData');

/**
 * @internal
 */
export const userLookupFieldFacet: Facet<string> = new Facet('userLookupField');

/**
 * @internal
 */
export const spPeoplePickerDataSourceFacet = new Facet<PeoplePickerDataSource>('spPeoplePickerDataSource');

export const spAggregatesFacet = new Facet<boolean>('spAggregates');

export const spFilterableSetFacet: Facet<{
  getFilteredItemSetKey: (params: {
    filterField: string,
    filterValues: string[],
    filterType: string,
    useLookupId: boolean
  }) => string;
  appliedFilters?: { [fieldName: string]: IFilter };
  filterParams?: string;
  filterList?: IFilter[];
}> = new Facet('spFilterableSetFacet');
/**
 * Json blob from the View's NewDocumentTemplates property containing information about templates and the "New" menu
 */
export const newDocumentTemplatesFacet: Facet<INewDocumentTemplate[]> = new Facet('newDocumentTemplates');

/** Contains properties for templates uploaded in modern UI */
export const newDocumentTemplateFacet: Facet<INewDocumentTemplate> = new Facet('newDocumentTemplate');

/**
 * Configuration facet to enable custom user-agent feature for GetListDataAsStream.
 * @public
 */
export const enableUserAgentConfigurationFacet = new Facet<boolean>('enableUserAgent');

/**
 * Configuration facet for layout type localStorage uses.
 * useQueryType to determine layoutMap index.
 */
export const spLayoutConfigurationFacet = new Facet<{
  (itemCache: IItemCache, props: { itemSetKey: string }): { queryType: number }
}>('spLayoutConfiguration');

/**
 * Grid init info for spGrid quick edit.
 */
export const gridInitInfoFacet = new Facet<IGridInitInfo>('gridInitInfo');

/** A call back function that will be called when we process list item response */
export const onProcessListItemResponseConfigurationFacet: Facet<{
  (payload: { itemSetKey: string; serverData: ISPGetItemResponse; isItemSetRequest: boolean }): IItemCacheThunkAction<void>
}> = new Facet('onProcessListItemResponseConfigurationFacet');

/**
 * Configuration facet to enable grouping for OneDrive document libraries.
 */
export const enableGroupByInOneDriveFacet: Facet<boolean> = new Facet('enableGroupByInOneDrive');

/**
 * Configuration facet to enable quick edit for document library and list.
 * Currently this is only working when for app host inside sharepoint site.
 */
export const enableQuickEditFacet: Facet<boolean> = new Facet('enableQuickEdit');

/**
 * Whether or not to force-enable Modern list schemas for all requests.
 */
export const enableModernListSchemasConfigurationFacet: Facet<boolean> = new Facet('enableModernListSchemasConfiguration');

/**
 * Configuration facet to enable modern column totals settings for document library and list.
 */
export const enableModernColumnTotalsSettingsFacet: Facet<boolean> = new Facet('enableModernColumnTotalsSettings');

/**
 * Facet to store signal to indicate whether complete exiting quick edit.
 * This signal will be resolved when spGrid complete all unsaved changes.
 */
export const completeExitingQuickEditSignalFacet = new Facet<Signal<void>>('completeExitingQuickEditSignal');

/**
 * Whether or not the SharePoint addon should attempt to map new Graph items back to SharePoint keys.
 */
export const enableMapGraphItemsToSharePointFacet = new Facet<boolean>('enableMapGraphItemsToSharePoint');

/**
 * Configuration facet to enable folder/media grouping splits when using Tiles layout.
 */
export const enableMediaGroupingConfigurationFacet = new Facet<boolean>('enableMediaGroupingConfiguration');

/**
 * Configuration facet to show edit item properties command and view item properties command for custom list.
 */
export const enableEditOrViewItemCommandConfigurationFacet = new Facet<boolean>('enableEditOrViewItemCommandConfigurationFacet');

/**
 * Configuration facet to show approve/reject command for library that has content approval enabled.
 * The new approveReject command allow bulk approve/reject.
 */
export const enableApproveRejectCommandConfigurationFacet = new Facet<boolean>('enableApproveRejectCommandConfiguration');
/**
 * Configuration facet to enable automatic launching of new windows to download malware-infected files.
 */
export const enableMalwareDownloadInNewWindowConfigurationFacet = new Facet<boolean>('enableMalwareDownloadInNewWindowConfiguration');

export const enableItemDetailsRowStatelessComponentEvaluationConfigurationFacet =
  new Facet<boolean>('enableItemDetailsRowStatelessComponentEvaluation');

/**
 * next/previous page token facet
 */
export const nextHrefFacet = new Facet<string>('nextHref');
export const prevHrefFacet = new Facet<string>('prevHref');

/**
 * Configuration facet to allow loading debug client-side extensions via query parameters.
 * customActions: JSON string describing the commandset and application extensions, with format of
 *    ICustomizedFieldsDict
 * fieldCustomizers: JSON string describing the field customizers, with format of
 *    ICustomActionExtensionDict
 */
export const enableDebugClientSideExtensionsFacet = new Facet<{
  customActions?: string;
  fieldCustomizers?: string;
}>('enableDebugClientSideExtensions');
